// ─────────────────────────────────────────────────────────────────────────────
// Aviso de que un filtro ACTIVO de la tira no aplica a ESTA sección.
//
// 🔴 Existe por una regla de producto explícita (2026-09-03): un filtro que no cambia nada es
// indistinguible, para quien mira, de una pantalla rota. La regla que ya seguía la tira —«un filtro
// que no filtra es peor que ausente»— se puede cumplir de dos maneras: ocultando el control (lo que
// se hizo con las tres pestañas de `TABS_WITHOUT_FILTER_BAR`) o explicándolo. Dentro de una pestaña
// que SÍ usa la tira no se puede ocultar el control por una sección, así que se explica.
//
// El aviso NO es un error: es informativo y así se anuncia (`role="note"`). Un `role="alert"` haría
// que el lector de pantalla interrumpiera la lectura en cada cambio de Fundamental.
// ─────────────────────────────────────────────────────────────────────────────
import { useTranslation } from 'react-i18next';

/** Los filtros de la tira que pueden no aplicar a una sección. Fecha y OU aplican en todas. */
export type FiltroDeTira = 'fundamental' | 'persona' | 'fechas';

/**
 * Por qué no aplica. Es una clave cerrada y no texto libre: obliga a que cada motivo nuevo pase por
 * los 7 locales en vez de colarse como una cadena suelta en español.
 */
export type MotivoNoAplica =
  | 'embudo'
  | 'historico'
  | 'audienciaCampana'
  | 'porBanda'
  | 'porOu'
  | 'redOu'
  /**
   * Punto 9 (revisión final, 2026-09-03) — «Brecha Generacional × Fundamental»
   * (`BrechaGeneracionalTable.tsx`) usaba `censo`, cuyo texto habla del CENSO de plantilla. Esa
   * tabla no sale del censo: sale de `useAnalyticsGenerational` (promedios de Pulso por generación y
   * Fundamental, escala 1-4) — el aviso estaba bien puesto, la explicación era de otra sección.
   */
  | 'promedioPulso'
  /**
   * 2026-09-04 — «Brillamos Juntos» (`ShineTab.tsx`): al conectar el Fundamental a `campaign-summary`
   * (commit `f6685471`) el aviso `audienciaCampana` se movió de cubrir los 5 KPIs a cubrir solo
   * «Total de plantilla», y en el traslado se perdió el aviso de persona para los otros cuatro
   * («Reconocimientos realizados», «Personas únicas reconocidas», «Personas únicas que reconocen»,
   * «Tasa de participación»): siguen sin aceptar persona, porque el endpoint `campaign-summary` no
   * recibe `personaId` (decisión de producto — con persona, «Personas únicas reconocidas» daría 1 y
   * la «tasa de participación» compararía a una persona contra la audiencia completa, un número sin
   * sentido bajo esa etiqueta). `audienciaCampana` no encaja aquí: su texto habla de la audiencia
   * CONVOCADA (fija sin importar el filtro), y estos cuatro no fallan por eso — si fallan es solo con
   * persona, no con Fundamental, que sí los acota desde el mismo commit.
   */
  | 'resumenCampana'
  /**
   * 2026-09-04 — Moderación. Al abrirle la barra de filtros (antes se ocultaba entera), el único que
   * no puede honrar es Fundamental: sus filas son CASOS reportados, y el motivo del reporte es texto
   * libre del reportante, no un Fundamental del reconocimiento. Los otros tres —OU, rango y
   * persona— sí se aplican.
   */
  | 'casosReportados'
  /**
   * 2026-09-24 — Brillamos Juntos con una campaña FINALIZADA (BJ-F5/BJ-D2, REPORTERIA-REGLAS.md §8): el
   * rango de fechas de la tira solo aplica a campañas activas; una cerrada se reporta completa.
   */
  | 'campanaFinalizada';

interface AvisoFiltroNoAplicaProps {
  /** Filtros ACTIVOS que esta sección no honra. Vacío ⇒ no se pinta nada. */
  readonly filtros: readonly FiltroDeTira[];
  readonly motivo: MotivoNoAplica;
}

/**
 * `FiltroDeTira` usa `'persona'` (español, a la par de `MotivoNoAplica`), pero la clave i18n del
 * filtro ya existía como `reports.filters.person` (la usa el chip de filtro activo de la tira,
 * `RecognitionAnalyticsView.tsx`). En vez de duplicar la traducción bajo dos claves en los 7
 * locales, se mapea aquí una vez.
 */
const CLAVE_DE_FILTRO: Readonly<Record<FiltroDeTira, string>> = {
  fundamental: 'fundamental',
  persona: 'person',
  fechas: 'dateRange',
};

export function AvisoFiltroNoAplica({ filtros, motivo }: AvisoFiltroNoAplicaProps) {
  const { t } = useTranslation('recognition');
  if (filtros.length === 0) return null;

  const nombres = filtros.map((filtro) => t(`reports.filters.${CLAVE_DE_FILTRO[filtro]}`));
  /*
   * Punto 4 (revisión final, 2026-09-03) — BLOQUEANTE (texto): con los DOS filtros activos (caso
   * real: `BloquePlantilla.tsx`, `BrechaGeneracionalTable.tsx`, `DailyTab.tsx`, `ShineTab.tsx`) el
   * texto salía «El filtro Fundamental · Persona no aplica…»: singular con dos sujetos, y un «·»
   * donde va la conjunción. `title_one`/`title_other` (plural de i18next, con `count`) resuelven la
   * concordancia en los 7 locales, y la conjunción propia de cada idioma (`reports.notApplicable.and`)
   * une los nombres — nunca ' · ', que no es una palabra en ningún idioma.
   */
  const listaDeFiltros = nombres.length > 1
    ? nombres.join(` ${t('reports.notApplicable.and')} `)
    : nombres[0];

  return (
    <p className="rr-filtro-no-aplica" role="note">
      <i className="fas fa-circle-info" aria-hidden="true" />
      <span>
        <strong>{t('reports.notApplicable.title', { count: filtros.length, filtros: listaDeFiltros })}</strong>{' '}
        {t(`reports.notApplicable.reason.${motivo}`)}
      </span>
    </p>
  );
}
